import { Box, Button, Slider, Typography } from "@mui/material";

import {
  type BoundaryStyle,
  MAX_BOUNDARY_WIDTH,
  MIN_BOUNDARY_WIDTH,
  sameBoundaryStyle,
} from "../../types/boundaryStyle";

interface BoundaryStyleEditorProps {
  value: BoundaryStyle;
  /** What "Reset" goes back to. */
  defaults: BoundaryStyle;
  onChange: (style: BoundaryStyle) => void;
  onReset: () => void;
  disabled?: boolean;
}

/**
 * Colour, width and opacity for one boundary line, with a sample of the
 * result. Every change is reported as it happens so the caller can preview it
 * on the map; the caller decides when to save.
 */
export default function BoundaryStyleEditor({ value, defaults, onChange, onReset, disabled = false }: BoundaryStyleEditorProps) {
  const row = (label: string, control: React.ReactNode) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Typography variant="body2" sx={{ width: 64, color: "text.secondary", flexShrink: 0 }}>
        {label}
      </Typography>
      {control}
    </Box>
  );
  const single = (v: number | number[]) => (Array.isArray(v) ? v[0] : v);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {row(
        "Colour",
        <Box
          component="input"
          type="color"
          value={value.color}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, color: e.target.value })}
          aria-label="Boundary colour"
          sx={{
            width: 40,
            height: 28,
            padding: 0,
            border: "1px solid rgba(148, 163, 184, 0.5)",
            borderRadius: "4px",
            background: "transparent",
            cursor: disabled ? "default" : "pointer",
            "&::-webkit-color-swatch-wrapper": { padding: 0 },
            "&::-webkit-color-swatch": { border: "none", borderRadius: "3px" },
          }}
        />
      )}
      {row(
        "Width",
        <Slider
          size="small"
          value={value.width}
          min={MIN_BOUNDARY_WIDTH}
          max={MAX_BOUNDARY_WIDTH}
          step={0.5}
          disabled={disabled}
          onChange={(_, v) => onChange({ ...value, width: single(v) })}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v} px`}
          aria-label="Boundary width"
          sx={{ flex: 1, color: "#00d4ad" }}
        />
      )}
      {row(
        "Opacity",
        <Slider
          size="small"
          value={value.opacity}
          min={0}
          max={1}
          step={0.05}
          disabled={disabled}
          onChange={(_, v) => onChange({ ...value, opacity: single(v) })}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
          aria-label="Boundary opacity"
          sx={{ flex: 1, color: "#00d4ad" }}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" sx={{ width: 64, color: "text.secondary", flexShrink: 0 }}>
          Sample
        </Typography>
        <Box
          sx={{
            flex: 1,
            height: 28,
            borderRadius: 1,
            // A checker behind the sample so opacity reads against something.
            backgroundImage:
              "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)",
            backgroundSize: "12px 12px",
            backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
            display: "flex",
            alignItems: "center",
            px: 1,
          }}
        >
          <Box sx={{ flex: 1, height: 0, borderTop: `${value.width}px solid ${value.color}`, opacity: value.opacity }} />
        </Box>
        <Button size="small" color="inherit" onClick={onReset} disabled={disabled || sameBoundaryStyle(value, defaults)}>
          Reset
        </Button>
      </Box>
    </Box>
  );
}
